import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useScroll, useTransform, useInView } from "framer-motion";
import { X, ArrowLeft as ArrowLeftIcon, ArrowRight as ArrowRightIcon } from "lucide-react";
import { API_BASE } from "@/lib/api";

/**
 * Resolve image path — supports absolute URLs, /api/media/{id}, or just id.
 */
function resolveImage(src) {
  if (!src) return null;
  if (src.startsWith("http")) return src;
  if (src.startsWith("/api/")) {
    // strip trailing /api if API_BASE already has one; API_BASE ends in /api
    return `${API_BASE.replace(/\/api$/, "")}${src}`;
  }
  return src;
}

/**
 * Signature line-by-line masked reveal — Awwwards style.
 */
export function MaskedLines({ lines, delay = 0, className = "", italicIndexes = [] }) {
  return (
    <>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.05, delay: delay + i * 0.11, ease: [0.22, 1, 0.36, 1] }}
            className={`block ${italicIndexes.includes(i) ? "italic font-normal" : ""} ${className}`}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </>
  );
}

/**
 * Fade-up on scroll (in-view).
 */
export function Reveal({ children, delay = 0, y = 40, className = "", once = true }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, amount: 0.15 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Fullscreen lightbox modal with keyboard nav (arrows + ESC).
 */
export function Lightbox({ images, index, onClose, onPrev, onNext }) {
  const isOpen = index !== null && index >= 0 && images[index] !== undefined;

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, onPrev, onNext]);

  if (!isOpen) return null;
  const src = resolveImage(images[index]);
  return createPortal(
    <AnimatePresence>
      <motion.div
        key="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
        data-testid="lightbox"
        className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
      >
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-6 md:p-8">
          <div className="font-mono-label text-white/60">
            <span className="text-white">{String(index + 1).padStart(2, "0")}</span>
            <span className="mx-2">/</span>
            <span>{String(images.length).padStart(2, "0")}</span>
          </div>
          <button
            onClick={onClose}
            data-testid="lightbox-close"
            className="border border-white/40 text-white p-3 hover:bg-white hover:text-black transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Image */}
        <motion.img
          key={index}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          src={src}
          alt=""
          className="max-w-[92vw] max-h-[82vh] object-contain"
        />

        {/* Nav arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={onPrev}
              data-testid="lightbox-prev"
              className="absolute left-6 md:left-10 top-1/2 -translate-y-1/2 border border-white/40 text-white p-4 hover:bg-white hover:text-black transition-colors"
              aria-label="Previous"
            >
              <ArrowLeftIcon size={18} />
            </button>
            <button
              onClick={onNext}
              data-testid="lightbox-next"
              className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 border border-white/40 text-white p-4 hover:bg-white hover:text-black transition-colors"
              aria-label="Next"
            >
              <ArrowRightIcon size={18} />
            </button>
          </>
        )}

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 flex items-center justify-between font-mono-label text-white/50">
          <span>[ GALERIE PORTOFOLIU ]</span>
          <span className="hidden md:inline">← / → NAVIGARE · ESC ÎNCHIDE</span>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

/**
 * 3-col responsive gallery with lazy loading + click-to-lightbox.
 */
export function Gallery({ images, onOpen }) {
  if (!images || images.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black border border-black">
      {images.map((src, i) => (
        <motion.button
          key={i}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
          onClick={() => onOpen(i)}
          data-testid={`gallery-item-${i}`}
          className="group relative aspect-[4/3] overflow-hidden bg-white text-left"
        >
          <img
            src={resolveImage(src)}
            loading="lazy"
            decoding="async"
            alt=""
            className="gallery-img w-full h-full object-cover group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" />
          <div className="absolute top-3 left-3 font-mono-label text-white/0 group-hover:text-white transition-colors duration-500">
            [ {String(i + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")} ]
          </div>
        </motion.button>
      ))}
    </div>
  );
}

/**
 * Parallax image wrapper for hero.
 */
export function ParallaxImage({ src, alt = "" }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.05, 1, 1.03]);
  return (
    <div ref={ref} className="relative w-full h-full overflow-hidden">
      <motion.img
        src={resolveImage(src)}
        alt={alt}
        style={{ y, scale }}
        className="w-full h-[115%] object-cover grayscale-img"
      />
      <div className="absolute inset-0 grain pointer-events-none" />
    </div>
  );
}

/**
 * Slow editorial marquee.
 */
export function EditorialMarquee({ text, tag = "" }) {
  const items = useMemo(() => Array.from({ length: 2 }, () => text), [text]);
  return (
    <div className="relative overflow-hidden border-y border-black py-6 md:py-8 bg-white">
      <div className="marquee-slow font-display font-medium uppercase tracking-tighter text-5xl md:text-8xl leading-none">
        {items.map((t, i) => (
          <span key={i} className="mx-10">
            {t}
            <span className="mx-8 text-black/25"></span>
          </span>
        ))}
      </div>
      {tag && (
        <div className="absolute right-6 top-1/2 -translate-y-1/2 font-mono-label text-black/40 hidden md:block">
          {tag}
        </div>
      )}
    </div>
  );
}
